import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  LinearProgress,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  TrendingUp,
  Assignment,
  Star,
  AccountBalanceWallet,
  CheckCircle,
  Schedule
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const P2PPartnerStats = ({ statistics }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const statsCards = [
    {
      title: 'Total Orders',
      value: statistics.total_orders,
      icon: Assignment,
      color: theme.palette.primary.main,
      bgColor: theme.palette.primary.light + '20',
    },
    {
      title: 'Completed Orders',
      value: statistics.completed_orders,
      icon: CheckCircle,
      color: theme.palette.success.main,
      bgColor: theme.palette.success.light + '20',
    },
    {
      title: 'Pending Orders',
      value: statistics.pending_orders,
      icon: Schedule,
      color: theme.palette.warning.main,
      bgColor: theme.palette.warning.light + '20',
    },
    {
      title: 'Active Rates',
      value: statistics.active_rates,
      icon: AccountBalanceWallet,
      color: theme.palette.info.main,
      bgColor: theme.palette.info.light + '20',
    },
    {
      title: 'Total Volume',
      value: formatCurrency(statistics.total_volume),
      icon: TrendingUp,
      color: theme.palette.secondary.main,
      bgColor: theme.palette.secondary.light + '20',
    },
    {
      title: 'Average Rating',
      value: `${statistics.average_rating}/5.0`,
      icon: Star,
      color: '#FFB400',
      bgColor: '#FFB40020',
      subtitle: `${statistics.total_ratings} ratings`,
    },
  ];

  const performanceCards = [
    {
      title: 'Success Rate',
      value: `${statistics.success_rate}%`,
      progress: statistics.success_rate,
      color: theme.palette.success.main,
    },
    {
      title: 'Monthly Volume',
      value: formatCurrency(statistics.monthly_volume),
      subtitle: 'This month',
      color: theme.palette.primary.main,
    },
    {
      title: 'Weekly Orders',
      value: statistics.weekly_orders,
      subtitle: 'This week',
      color: theme.palette.info.main,
    },
  ];

  return (
    <Box>
      {/* Main Statistics */}
      <Grid container spacing={isMobile ? 2 : 3} sx={{ mb: 3 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={6} sm={4} md={3} lg={2} key={index}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                sx={{
                  height: isMobile ? '140px' : '160px', // Fixed height for consistent sizing
                  minHeight: isMobile ? '140px' : '160px',
                  background: `linear-gradient(135deg, ${stat.bgColor} 0%, ${stat.color}10 100%)`,
                  border: `1px solid ${stat.color}30`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 25px ${stat.color}30`,
                  },
                }}
              >
                <CardContent sx={{ p: isMobile ? 1.5 : 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: isMobile ? 1 : 1.5 }}>
                    <Avatar
                      sx={{
                        bgcolor: stat.color,
                        width: isMobile ? 32 : 40,
                        height: isMobile ? 32 : 40,
                        mr: isMobile ? 1 : 2,
                      }}
                    >
                      <stat.icon sx={{ fontSize: isMobile ? 16 : 20 }} />
                    </Avatar>
                  </Box>
                  <Typography
                    variant={isMobile ? "h6" : "h5"}
                    fontWeight="bold"
                    color={stat.color}
                    gutterBottom
                    sx={{ lineHeight: 1.1 }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant={isMobile ? "caption" : "body2"}
                    color="text.secondary"
                    fontWeight="medium"
                    sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem', lineHeight: 1.2 }}
                  >
                    {stat.title}
                  </Typography>
                  {stat.subtitle && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ 
                        display: 'block', 
                        mt: 0.5, 
                        fontSize: isMobile ? '0.6rem' : '0.75rem',
                        lineHeight: 1.1 
                      }}
                    >
                      {stat.subtitle}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Performance Metrics */}
      <Grid container spacing={isMobile ? 2 : 3}>
        {performanceCards.map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: (statsCards.length + index) * 0.1 }}
            >
              <Card
                sx={{
                  height: isMobile ? '120px' : '140px', // Fixed height for consistent sizing
                  minHeight: isMobile ? '120px' : '140px',
                  background: theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardContent sx={{ p: isMobile ? 2 : 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                  <Typography
                    variant={isMobile ? "h5" : "h4"}
                    fontWeight="bold"
                    color={metric.color}
                    gutterBottom
                    sx={{ lineHeight: 1.1 }}
                  >
                    {metric.value}
                  </Typography>
                  <Typography
                    variant={isMobile ? "body2" : "h6"}
                    fontWeight="medium"
                    color="text.primary"
                    gutterBottom
                    sx={{ fontSize: isMobile ? '0.8rem' : '1.25rem', lineHeight: 1.2 }}
                  >
                    {metric.title}
                  </Typography>
                  {metric.subtitle && (
                    <Typography
                      variant={isMobile ? "caption" : "body2"}
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}
                    >
                      {metric.subtitle}
                    </Typography>
                  )}
                  {metric.progress !== undefined && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={metric.progress}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'rgba(0,0,0,0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor: metric.color,
                            borderRadius: 4,
                          },
                        }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default P2PPartnerStats;
